// Dashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FiTrendingUp, FiTrendingDown, FiActivity, FiCalendar, FiTarget } from 'react-icons/fi';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

const DASHBOARD_CACHE_PREFIX = 'dashboard-cache-v1:';
const DEFAULT_TTL_MS = 5 * 60 * 1000;

const buildCacheKey = (resource, params = '') => `${DASHBOARD_CACHE_PREFIX}${resource}:${params}`;

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (key, data, ttlMs = DEFAULT_TTL_MS) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      expiresAt: Date.now() + ttlMs,
      data
    }));
  } catch {
    // Ignore quota and serialization issues and keep network path as fallback.
  }
};

const getWithCache = async (url, { cacheKey, ttlMs = DEFAULT_TTL_MS } = {}) => {
  if (cacheKey) {
    const cached = readCache(cacheKey);
    if (cached !== null) {
      return { data: cached };
    }
  }

  const response = await api.get(url);
  if (cacheKey) {
    writeCache(cacheKey, response.data, ttlMs);
  }
  return response;
};

function Dashboard() {
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [isFilterPickerOpen, setIsFilterPickerOpen] = useState(false);
  const [userName] = useState(JSON.parse(localStorage.getItem('user'))?.first_name || 'Usuário');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async (range) => {
    setIsLoading(true);
    try {
      const transactionParams = new URLSearchParams();
      if (range?.from) transactionParams.append('start_date', format(range.from, 'y-MM-dd'));
      if (range?.to) transactionParams.append('end_date', format(range.to, 'y-MM-dd'));
      const transactionParamsStr = transactionParams.toString();

      const budgetParams = new URLSearchParams({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1
      });
      const budgetParamsStr = budgetParams.toString();

      const [
        transactionsResponse,
        balanceHistoryResponse,
        monthlySummaryResponse,
        budgetsResponse
      ] = await Promise.all([
        getWithCache(`/transactions/?${transactionParamsStr}`, {
          cacheKey: buildCacheKey('transactions', transactionParamsStr || 'all'),
          ttlMs: 60 * 1000
        }),
        getWithCache('/reports/balance-evolution', {
          cacheKey: buildCacheKey('report-balance-evolution', 'default')
        }),
        getWithCache('/reports/monthly-summary', {
          cacheKey: buildCacheKey('report-monthly-summary', 'default')
        }),
        getWithCache(`/budgets/?${budgetParamsStr}`, {
          cacheKey: buildCacheKey('budgets', budgetParamsStr)
        })
      ]);

      setTransactions(transactionsResponse.data);
      setBalanceHistory(balanceHistoryResponse.data);
      setMonthlySummary(monthlySummaryResponse.data);
      setBudgets(budgetsResponse.data.filter(b => b.budget_amount > 0));

    } catch (error) {
      console.error("Erro ao buscar dados do dashboard:", error);
      toast.error("Não foi possível carregar os dados do dashboard.");
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData(dateRange);
  }, [dateRange, fetchData]);

  const handleFilterSubmit = () => {
    setIsFilterPickerOpen(false);
    fetchData(dateRange);
  };

  const handleClearFilters = () => {
    setDateRange({ from: undefined, to: undefined });
  };

  const summary = useMemo(() => {
    return transactions.reduce((acc, transaction) => {
      if (transaction.type === 'INCOME') acc.totalIncome += transaction.amount;
      else acc.totalExpense += transaction.amount;

      if (transaction.status === 'CONFIRMED') {
        if (transaction.type === 'INCOME') acc.confirmedIncome += transaction.amount;
        else acc.confirmedExpense += transaction.amount;
      }
      return acc;
    }, { confirmedIncome: 0, confirmedExpense: 0, totalIncome: 0, totalExpense: 0 });
  }, [transactions]);

  const currentBalance = summary.confirmedIncome - summary.confirmedExpense;
  const projectedBalance = summary.totalIncome - summary.totalExpense;

  const chartData = useMemo(() => {
    const expenseData = transactions
      .filter(t => t.status === 'CONFIRMED' && t.type === 'EXPENSE')
      .reduce((acc, t) => {
        const category = t.category?.name || 'Sem Categoria';
        if (!acc[category]) acc[category] = 0;
        acc[category] += t.amount;
        return acc;
      }, {});
    return Object.entries(expenseData).map(([name, value]) => ({ name, value }));
  }, [transactions]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold font-manrope tracking-tight text-on-surface">Bem-vindo de volta, {userName}!</h2>
          <p className="text-secondary text-sm font-medium">Aqui está o resumo financeiro das suas contas hoje.</p>
        </div>

        <div className="flex items-center space-x-3 bg-surface-container-low p-1.5 rounded-xl border border-outline-variant/10 relative">
          <button
            onClick={() => setIsFilterPickerOpen(!isFilterPickerOpen)}
            className="flex items-center space-x-2 px-4 py-2 bg-surface-container-lowest text-on-surface-variant text-sm font-medium rounded-lg shadow-sm hover:bg-slate-50 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">calendar_today</span>
            <span>{dateRange.from ? dateRange.to ? `${format(dateRange.from, 'dd/MM/y')} - ${format(dateRange.to, 'dd/MM/y')}` : format(dateRange.from, 'dd/MM/y') : 'Todo o período'}</span>
          </button>

          {isFilterPickerOpen && (
            <div className="absolute top-14 right-0 z-50 bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden">
              <DayPicker mode="range" selected={dateRange} onSelect={setDateRange} locale={ptBR} numberOfMonths={2} />
              <div className="flex justify-end p-3 border-t space-x-2 bg-slate-50">
                <button onClick={handleClearFilters} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg font-medium transition-colors">Limpar</button>
                <button onClick={handleFilterSubmit} className="px-4 py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors">Aplicar</button>
              </div>
            </div>
          )}

          <button className="p-2 hover:bg-slate-200 rounded-lg transition-colors text-slate-500">
            <span className="material-symbols-outlined">filter_list</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <ClipLoader color="#3525cd" size={60} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-surface-container-lowest p-6 rounded-xl border-l-4 border-primary shadow-sm hover:translate-y-[-4px] transition-transform duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <span className="material-symbols-outlined text-primary"></span>
                </div>
                {summary.confirmedIncome > 0 && <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">+</span>}
              </div>
              <p className="text-sm font-medium text-secondary mb-1">Receitas Confirmadas</p>
              <h3 className="text-2xl font-bold font-manrope">{formatCurrency(summary.confirmedIncome)}</h3>
            </div>

            <div className="bg-surface-container-lowest p-6 rounded-xl border-l-4 border-error shadow-sm hover:translate-y-[-4px] transition-transform duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-error/10 rounded-lg">
                  <span className="material-symbols-outlined text-error">trending_down</span>
                </div>
                {summary.confirmedExpense > 0 && <span className="text-xs font-bold text-error bg-error/10 px-2 py-1 rounded">-</span>}
              </div>
              <p className="text-sm font-medium text-secondary mb-1">Despesas Confirmadas</p>
              <h3 className="text-2xl font-bold font-manrope text-on-surface">{formatCurrency(summary.confirmedExpense)}</h3>
            </div>

            <div className="bg-surface-container-lowest p-6 rounded-xl border-l-4 border-[#0088FE] shadow-sm hover:translate-y-[-4px] transition-transform duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <span className="material-symbols-outlined text-blue-500">monitoring</span>
                </div>
                <div className="flex space-x-1">
                  <div className="w-1 h-3 bg-blue-200 rounded-full"></div>
                  <div className="w-1 h-5 bg-blue-400 rounded-full"></div>
                  <div className="w-1 h-4 bg-blue-600 rounded-full"></div>
                </div>
              </div>
              <p className="text-sm font-medium text-secondary mb-1">Balanço Atual</p>
              <h3 className="text-2xl font-bold font-manrope text-[#0088FE]">{formatCurrency(currentBalance)}</h3>
            </div>

            <div className="bg-surface-container-lowest p-6 rounded-xl border-l-4 border-slate-900 shadow-sm hover:translate-y-[-4px] transition-transform duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <span className="material-symbols-outlined text-slate-900">ads_click</span>
                </div>
              </div>
              <p className="text-sm font-medium text-secondary mb-1">Balanço Projetado</p>
              <h3 className="text-2xl font-bold font-manrope">{formatCurrency(projectedBalance)}</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2 bg-surface-container-lowest p-8 rounded-xl shadow-sm">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h4 className="text-xl font-bold font-manrope text-on-surface">Evolução do Saldo</h4>
                  <p className="text-sm text-secondary">Últimos 30 dias de performance financeira</p>
                </div>
                <div className="flex space-x-4">
                  <span className="flex items-center text-xs font-semibold text-primary"><span className="w-2 h-2 rounded-full bg-primary mr-2"></span> Saldo</span>
                  <span className="flex items-center text-xs font-semibold text-slate-300"><span className="w-2 h-2 rounded-full bg-slate-300 mr-2"></span> Projeção</span>
                </div>
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={balanceHistory} margin={{ top: 5, right: 0, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(tick) => {
                      const date = new Date(tick);
                      return `Sem: ${Math.ceil(date.getDate() / 7)}`;
                    }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(tick) => `${tick / 1000}k`} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} formatter={(value) => formatCurrency(value)} />
                    <Line type="monotone" dataKey="balance" stroke="#3525cd" strokeWidth={4} dot={false} activeDot={{ r: 6, strokeWidth: 0 }} />
                    <Line type="monotone" dataKey="projection" stroke="#cbd5e1" strokeWidth={3} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-surface-container-lowest p-8 rounded-xl shadow-sm flex flex-col">
              <h4 className="text-xl font-bold font-manrope text-on-surface mb-1">Despesas</h4>
              <p className="text-sm text-secondary mb-4">Distribuição mensal</p>
              <div className="flex-1 flex flex-col items-center justify-center">
                <div style={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={70} outerRadius={90} cornerRadius={4} paddingAngle={2}>
                        {chartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full mt-4 space-y-3">
                  {chartData.length > 0 ? chartData.slice(0, 3).map((entry, index) => {
                    const total = chartData.reduce((sum, item) => sum + item.value, 0);
                    const percent = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                    return (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <div className="flex items-center text-slate-600 font-medium"><span className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span> {entry.name}</div>
                        <span className="font-bold text-slate-800">{percent}%</span>
                      </div>
                    )
                  }) : <p className="text-xs text-slate-400 text-center">Nenhum dado</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 bg-surface-container-lowest p-8 rounded-xl shadow-sm flex flex-col">
              <h4 className="text-xl font-bold font-manrope text-on-surface mb-1">Acompanhamento</h4>
              <p className="text-sm text-secondary mb-8">Mês Atual</p>
              <div className="space-y-6 flex-1">
                {budgets.length > 0 ? budgets.slice(0, 3).map(budget => {
                  const percentage = budget.budget_amount > 0 ? (budget.spent_amount / budget.budget_amount) * 100 : 0;
                  const progressColor = percentage > 100 ? '#ba1a1a' : percentage > 80 ? '#4f46e5' : '#3525cd';
                  const bgClass = percentage > 100 ? 'bg-red-100' : 'bg-slate-100';
                  return (
                    <div key={budget.category_id}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-semibold text-slate-700">{budget.category_name}</span>
                        <span className={`font-medium ${percentage > 100 ? 'text-error' : 'text-slate-500'}`}>
                          R$ {Math.round(budget.spent_amount / 1000)}k / {Math.round(budget.budget_amount / 1000)}k
                        </span>
                      </div>
                      <div className={`h-2.5 ${bgClass} rounded-full overflow-hidden`}>
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(percentage, 100)}%`, backgroundColor: progressColor }}></div>
                      </div>
                    </div>
                  )
                }) : <p className="text-sm text-slate-500 text-center py-4">Nenhum orçamento definido</p>}

              </div>
              <button onClick={() => navigate('/app/budgets')} className="w-full py-3 mt-6 text-sm font-bold text-primary hover:bg-indigo-50 rounded-xl transition-colors border border-transparent hover:border-indigo-100">
                Ver todos
              </button>
            </div>

            <div className="lg:col-span-2 bg-surface-container-lowest p-8 rounded-xl shadow-sm">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h4 className="text-xl font-bold font-manrope text-on-surface">Balanço</h4>
                  <p className="text-sm text-secondary">Últimos meses</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1.5"><span className="w-4 h-4 rounded bg-slate-200"></span><span className="text-xs font-semibold text-slate-500">Despesas</span></div>
                  <div className="flex items-center space-x-1.5"><span className="w-4 h-4 rounded bg-primary"></span><span className="text-xs font-semibold text-slate-700">Receitas</span></div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlySummary} margin={{ top: 5, right: 0, left: -20, bottom: 5 }} barSize={24} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }} tickFormatter={(tick) => {
                      const index = new Date(tick).getMonth();
                      const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
                      return months[index] || format(parse(tick, 'yyyy-MM', new Date()), 'MMM', { locale: ptBR }).toUpperCase();
                    }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(tick) => `R$${tick / 1000}k`} />
                    <Tooltip cursor={{ fill: 'rgba(53, 37, 205, 0.04)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="expense" name="Despesas" fill="#e2e8f0" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="income" name="Receitas" fill="#3525cd" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Dashboard;